import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Separator } from "@/components/ui/separator";
import { Check, X, Clock } from "lucide-react";
import api, { handleApiError, formatCurrency, formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import { usePermission } from "@/hooks/usePermission";

export default function CompanyApprovals() {
  const { canEdit } = usePermission("aprovacoes");
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const fetchPending = useCallback(async () => {
    try { const res = await api.get("/anticipations/pending"); setPending(res.data); } catch (err) { toast.error(handleApiError(err, "Erro ao carregar aprovações")); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await api.post(`/anticipations/${id}/approve`);
      const item = pending.find(p => p.id === id);
      toast.success(`Antecipação de ${item?.employee_name || "colaborador"} aprovada`);
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao aprovar");
    }
    setProcessing(null);
  };

  const handleReject = async (id) => {
    setProcessing(id);
    try {
      await api.post(`/anticipations/${id}/reject`);
      const item = pending.find(p => p.id === id);
      toast.success(`Antecipação de ${item?.employee_name || "colaborador"} recusada`);
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao recusar");
    }
    setProcessing(null);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen"><div className="loading-spinner w-8 h-8" /></div>
  );

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-5 lg:space-y-6" data-testid="company-approvals">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Aprovações Pendentes</h1>
        <p className="text-sm text-muted-foreground mt-1">{pending.length} solicitaç{pending.length !== 1 ? "ões" : "ão"} pendente{pending.length !== 1 ? "s" : ""}</p>
      </div>

      {pending.length === 0 ? (
        <Card className="border-white/5" data-testid="company-approvals-empty">
          <CardContent className="py-12 text-center">
            <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Nenhuma solicitação pendente de aprovação</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4 stagger-children">
          {pending.map((a) => (
            <Card key={a.id} data-testid={`approval-item-${a.id}`} className="border-white/5 animate-fade-in card-interactive">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-semibold text-lg">{a.employee_name}</p>
                    <p className="text-xs text-muted-foreground mt-1">Solicitado em {formatDateTime(a.created_at)}</p>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
                <Separator className="bg-white/5 my-4" />
                <div className="grid grid-cols-3 gap-4 mb-5">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Valor Bruto</p>
                    <p className="text-base font-semibold">{formatCurrency(a.total_bruto)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Valor Líquido</p>
                    <p className="text-base font-semibold text-emerald-400">{formatCurrency(a.total_liquido)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Títulos</p>
                    <p className="text-base font-semibold">{a.titulo_count}</p>
                  </div>
                </div>
                {canEdit && (
                <div className="flex items-center gap-3 justify-end">
                  <Button variant="outline" data-testid={`reject-btn-${a.id}`}
                    onClick={() => handleReject(a.id)} disabled={processing === a.id}
                    className="border-red-500/20 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                    <X className="w-4 h-4 mr-1" /> Recusar
                  </Button>
                  <Button data-testid={`approve-btn-${a.id}`}
                    onClick={() => handleApprove(a.id)} disabled={processing === a.id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    {processing === a.id ? <div className="loading-spinner" /> : <><Check className="w-4 h-4 mr-1" /> Aprovar</>}
                  </Button>
                </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
